import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, ShoppingBag, Tag, Clock, ChevronDown, Plus, ExternalLink, User } from 'lucide-react';
import theme from '../theme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreatorUtilitiesMarketplace = ({ user }) => {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [attachModal, setAttachModal] = useState(null);
  const [attachingId, setAttachingId] = useState(null);
  const [selectedPortfolio, setSelectedPortfolio] = useState('');
  const [attachNotes, setAttachNotes] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [listingsRes, portfoliosRes] = await Promise.all([
        axios.get(`${API}/creator/utilities-marketplace`, { headers }),
        axios.get(`${API}/creator/portfolios`, { headers })
      ]);
      setListings(listingsRes.data.listings || []);
      setPortfolios(portfoliosRes.data.portfolios || []);
    } catch (err) {
      console.error('Error fetching marketplace:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (searchQuery) params.append('q', searchQuery);
      if (categoryFilter) params.append('category', categoryFilter);
      const res = await axios.get(`${API}/creator/utilities-marketplace?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setListings(res.data.listings || []);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAttach = async () => {
    if (!selectedPortfolio || !attachModal) return;
    setAttachingId(attachModal.id);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/creator/portfolios/${selectedPortfolio}/utilities/attach`, {
        listing_id: attachModal.id,
        custom_notes: attachNotes || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      setAttachModal(null);
      setSelectedPortfolio('');
      setAttachNotes('');
      alert('Utility attached successfully!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to attach utility');
    } finally {
      setAttachingId(null);
    }
  };

  const getPricingBadge = (model, amount, currency) => {
    if (model === 'FREE') return { label: 'Free', color: theme.colors.status.success };
    if (model === 'PAID') return { label: `${currency} ${amount || '—'}`, color: theme.colors.neon.blue };
    return { label: 'Rev Share', color: theme.colors.accent.salmon };
  };

  const categories = [...new Set(listings.flatMap(l => l.category_tags || []))];

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: theme.colors.text.secondary }}>Loading marketplace...</div>;
  }

  return (
    <div style={{ padding: '40px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
        <div>
          <h1 data-testid="marketplace-title" style={{
            fontFamily: theme.fonts.heading,
            fontSize: '28px',
            fontWeight: 700,
            color: theme.colors.text.primary,
            marginBottom: '6px'
          }}>
            Utilities Marketplace
          </h1>
          <p style={{ fontSize: '14px', color: theme.colors.text.secondary }}>
            Browse and attach utility listings to your portfolios
          </p>
        </div>
        <button
          data-testid="publish-utility-btn"
          onClick={() => navigate('/creator/publish-utility')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            background: theme.colors.primary,
            color: '#FFFFFF',
            border: 'none',
            borderRadius: theme.radius.sm,
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          <Plus size={18} />
          Publish Utility
        </button>
      </div>

      {/* Search & Filter */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '24px',
        flexWrap: 'wrap'
      }}>
        <div style={{ flex: 1, minWidth: '240px', position: 'relative' }}>
          <Search size={16} style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: theme.colors.text.muted
          }} />
          <input
            data-testid="marketplace-search"
            type="text"
            placeholder="Search utilities..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            style={{
              width: '100%',
              padding: '10px 12px 10px 36px',
              border: `1px solid ${theme.colors.borders}`,
              borderRadius: theme.radius.sm,
              fontSize: '14px',
              background: theme.colors.bg.card,
              color: theme.colors.text.primary,
              outline: 'none'
            }}
          />
        </div>
        {categories.length > 0 && (
          <select
            data-testid="marketplace-category-filter"
            value={categoryFilter}
            onChange={(e) => { setCategoryFilter(e.target.value); setTimeout(handleSearch, 100); }}
            style={{
              padding: '10px 16px',
              border: `1px solid ${theme.colors.borders}`,
              borderRadius: theme.radius.sm,
              fontSize: '14px',
              background: theme.colors.bg.card,
              color: theme.colors.text.primary,
              cursor: 'pointer'
            }}
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        )}
        <button
          data-testid="marketplace-search-btn"
          onClick={handleSearch}
          style={{
            padding: '10px 20px',
            background: theme.colors.bg.tertiary,
            color: theme.colors.text.primary,
            border: `1px solid ${theme.colors.borders}`,
            borderRadius: theme.radius.sm,
            fontSize: '14px',
            fontWeight: 500,
            cursor: 'pointer'
          }}
        >
          Search
        </button>
      </div>

      {/* Listings Grid */}
      {listings.length === 0 ? (
        <div style={{
          background: theme.colors.bg.card,
          borderRadius: theme.radius.card,
          padding: '60px',
          textAlign: 'center',
          border: `1px solid ${theme.colors.borders}`
        }}>
          <ShoppingBag size={48} style={{ color: theme.colors.text.muted, marginBottom: '16px' }} />
          <h3 style={{ color: theme.colors.text.primary, fontSize: '18px', marginBottom: '8px' }}>
            No Listings Yet
          </h3>
          <p style={{ color: theme.colors.text.secondary, fontSize: '14px', marginBottom: '20px' }}>
            Be the first to publish a utility listing
          </p>
          <button
            onClick={() => navigate('/creator/publish-utility')}
            style={{
              padding: '10px 24px',
              background: theme.colors.primary,
              color: '#FFFFFF',
              border: 'none',
              borderRadius: theme.radius.sm,
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Publish First Utility
          </button>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: '20px'
        }}>
          {listings.map((listing) => {
            const pricing = getPricingBadge(listing.pricing_model, listing.price_amount, listing.currency);
            return (
              <div
                key={listing.id}
                data-testid={`listing-card-${listing.id}`}
                onClick={() => navigate(`/creator/listings/${listing.id}`)}
                style={{
                  background: theme.colors.bg.card,
                  borderRadius: theme.radius.card,
                  border: `1px solid ${theme.colors.borders}`,
                  padding: '24px',
                  transition: 'all 0.2s',
                  boxShadow: theme.colors.shadows.sm,
                  cursor: 'pointer',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.boxShadow = theme.colors.shadows.md;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.boxShadow = theme.colors.shadows.sm;
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {/* Top row: title + pricing */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: theme.colors.text.primary,
                    flex: 1,
                    marginRight: '8px'
                  }}>
                    {listing.title}
                  </h3>
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    background: `${pricing.color}15`,
                    color: pricing.color,
                    whiteSpace: 'nowrap'
                  }}>
                    {pricing.label}
                  </span>
                </div>

                {/* Description */}
                <p style={{
                  fontSize: '13px',
                  color: theme.colors.text.secondary,
                  lineHeight: '1.5',
                  marginBottom: '16px',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}>
                  {listing.description}
                </p>

                {/* Provider + Type */}
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: theme.colors.text.muted }}>
                    <User size={14} />
                    {listing.provider_name}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: theme.colors.text.muted }}>
                    <Tag size={14} />
                    {listing.fulfillment_type}
                  </div>
                  {listing.duration_days && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: theme.colors.text.muted }}>
                      <Clock size={14} />
                      {listing.duration_days}d
                    </div>
                  )}
                </div>

                {/* Tags */}
                {listing.category_tags?.length > 0 && (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
                    {listing.category_tags.map(tag => (
                      <span key={tag} style={{
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '11px',
                        background: theme.colors.bg.tertiary,
                        color: theme.colors.text.secondary
                      }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Action */}
                <button
                  data-testid={`attach-btn-${listing.id}`}
                  onClick={(e) => { e.stopPropagation(); setAttachModal(listing); }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: `${theme.colors.primary}10`,
                    color: theme.colors.primary,
                    border: `1px solid ${theme.colors.primary}40`,
                    borderRadius: theme.radius.sm,
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.background = theme.colors.primary; e.currentTarget.style.color = '#fff'; }}
                  onMouseOut={(e) => { e.currentTarget.style.background = `${theme.colors.primary}10`; e.currentTarget.style.color = theme.colors.primary; }}
                >
                  Attach to Portfolio
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Attach Modal */}
      {attachModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}
          onClick={() => setAttachModal(null)}
        >
          <div
            data-testid="attach-modal"
            style={{
              background: theme.colors.bg.card,
              borderRadius: theme.radius.card,
              padding: '32px',
              maxWidth: '480px',
              width: '90%',
              boxShadow: theme.colors.shadows.xl
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{
              fontSize: '18px',
              fontWeight: 700,
              color: theme.colors.text.primary,
              marginBottom: '8px'
            }}>
              Attach "{attachModal.title}"
            </h3>
            <p style={{ fontSize: '13px', color: theme.colors.text.secondary, marginBottom: '24px' }}>
              Select which portfolio to attach this utility to.
            </p>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '13px', fontWeight: 500, color: theme.colors.text.primary, display: 'block', marginBottom: '6px' }}>
                Portfolio
              </label>
              <select
                data-testid="attach-portfolio-select"
                value={selectedPortfolio}
                onChange={(e) => setSelectedPortfolio(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${theme.colors.borders}`,
                  borderRadius: theme.radius.sm,
                  fontSize: '14px',
                  background: theme.colors.bg.card,
                  color: theme.colors.text.primary
                }}
              >
                <option value="">Select a portfolio...</option>
                {portfolios.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ fontSize: '13px', fontWeight: 500, color: theme.colors.text.primary, display: 'block', marginBottom: '6px' }}>
                Notes (optional)
              </label>
              <textarea
                data-testid="attach-notes"
                value={attachNotes}
                onChange={(e) => setAttachNotes(e.target.value)}
                placeholder="Any custom notes for this attachment..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${theme.colors.borders}`,
                  borderRadius: theme.radius.sm,
                  fontSize: '14px',
                  background: theme.colors.bg.card,
                  color: theme.colors.text.primary,
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                data-testid="attach-cancel-btn"
                onClick={() => setAttachModal(null)}
                style={{
                  padding: '10px 20px',
                  background: 'transparent',
                  color: theme.colors.text.secondary,
                  border: `1px solid ${theme.colors.borders}`,
                  borderRadius: theme.radius.sm,
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                data-testid="attach-confirm-btn"
                onClick={handleAttach}
                disabled={!selectedPortfolio || attachingId}
                style={{
                  padding: '10px 24px',
                  background: selectedPortfolio ? theme.colors.primary : theme.colors.bg.tertiary,
                  color: selectedPortfolio ? '#FFFFFF' : theme.colors.text.muted,
                  border: 'none',
                  borderRadius: theme.radius.sm,
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: selectedPortfolio ? 'pointer' : 'not-allowed',
                  opacity: attachingId ? 0.7 : 1
                }}
              >
                {attachingId ? 'Attaching...' : 'Confirm Attach'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatorUtilitiesMarketplace;
